import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { DateTime, Interval } from 'luxon';

dayjs.extend(isoWeek);
dayjs.extend(isTomorrow);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const luxonDays = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

// eslint-disable-next-line import/prefer-default-export
export const formatLatestMessageTimestamp = (timestamp) => {
  const messageTimestamp = DateTime.fromJSDate(timestamp);
  const today = DateTime.now();

  let formattedTimestamp;

  if (messageTimestamp.hasSame(today, 'day')) {
    formattedTimestamp = messageTimestamp.toFormat('h:mm a');
  } else if (messageTimestamp.hasSame(today.minus({ days: 1 }), 'day')) {
    formattedTimestamp = 'Yesterday';
  } else if (messageTimestamp.hasSame(today, 'weeks')) {
    formattedTimestamp = messageTimestamp.toFormat('cccc');
  } else if (messageTimestamp.hasSame(today, 'years')) {
    formattedTimestamp = messageTimestamp.toFormat('M/dd');
  } else {
    formattedTimestamp = messageTimestamp.toFormat('M/dd/yy');
  }

  return formattedTimestamp;
};

export const isToday = (date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const wasYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

export const datesHaveDifference = (date1, date2) =>
  date1.getDate() !== date2.getDate() ||
  date1.getMonth() !== date2.getMonth() ||
  date1.getFullYear() !== date2.getFullYear();

const isLuxonDateTomorrow = (date) => {
  if (!date) {
    return false;
  }

  const tomorrow = DateTime.now().plus({ days: 1 });

  return date.hasSame(tomorrow, 'day');
};

export const formatAutoReplyExpiration = (autoReplyExpirationDate) => {
  let luxonExpiration;

  if (autoReplyExpirationDate) {
    const expirationDate = new Date(autoReplyExpirationDate); // remove conversion to JS date
    const luxonDate = DateTime.fromJSDate(expirationDate);

    luxonExpiration = isLuxonDateTomorrow(luxonDate)
      ? luxonDate.toFormat('LL/dd hh:mm a')
      : luxonDate.toFormat('hh:mm a');
  }

  return luxonExpiration;
};

const checkRecurringStatus = (incomingRecurringStatus, expirationTime) => {
  const recurringStatus = incomingRecurringStatus?.split(';');
  const days = recurringStatus[0]?.split(',').map((day) => day.trim());
  const hours = recurringStatus[1]?.split('-').map((hour) => hour.trim());

  const customAutoReplyExpirtaion = DateTime.fromJSDate(new Date(expirationTime));

  return days.some((day) => {
    let startDate = DateTime.fromFormat(hours[0], 'h:mm a');
    startDate = startDate.set({ weekday: luxonDays[day] });

    let endDate = DateTime.fromFormat(hours[1], 'h:mm a');
    endDate = endDate.set({ weekday: luxonDays[day] });

    const expirationInterval = Interval.fromDateTimes(DateTime.now(), customAutoReplyExpirtaion);

    if (startDate >= endDate) {
      let startOfDay = DateTime.fromFormat('12:00 AM', 'h:mm a', {});
      startOfDay = startOfDay.set({ weekday: luxonDays[day] });

      let endOfDay = DateTime.fromFormat('11:59 PM', 'h:mm a', {});
      endOfDay = endOfDay.set({ weekday: luxonDays[day] });

      const scheduledInterval1 = Interval.fromDateTimes(startOfDay, endDate);
      const scheduledInterval2 = Interval.fromDateTimes(startDate, endOfDay);

      return (
        expirationInterval.overlaps(scheduledInterval1) ||
        expirationInterval.overlaps(scheduledInterval2)
      );
    }

    const scheduledInterval = Interval.fromDateTimes(startDate, endDate);

    return expirationInterval.overlaps(scheduledInterval);
  });
};

export const checkIfScheduledAutoReplyIsOverriddenByCustomAwayMessage = (
  activeCustomAwayMessage,
  scheduledAutoReply
) => {
  if (!activeCustomAwayMessage) {
    return false;
  }

  if (!activeCustomAwayMessage.ExpirationTime) {
    return true;
  }

  const checkResults = scheduledAutoReply.RecurringStatus.split('|').map((recurringStatus) =>
    checkRecurringStatus(recurringStatus, activeCustomAwayMessage.ExpirationTime)
  );

  return checkResults.some((isOverridden) => isOverridden);
};

export const hasAwayMessageAutoReplyExpired = (expirationTime) => {
  if (!expirationTime) {
    return false;
  }

  return dayjs(expirationTime).isBefore(dayjs());
};

export const parseRecurringStatus = (recurringStatus) => {
  const statuses = recurringStatus.split('|');

  return statuses.map((status) => {
    const [daysString, timesString] = status.split('; ');

    const days = daysString.split(', ');
    const [startTime, endTime] = timesString.split(' - ');

    return {
      days,
      startTime,
      endTime,
    };
  });
};

const getIntervalFromTimes = (startTime, endTime, day) => {
  let startDate = DateTime.fromFormat(startTime, 'h:mm a');
  startDate = startDate.set({ weekday: luxonDays[day] });

  let endDate = DateTime.fromFormat(endTime, 'h:mm a');
  endDate = endDate.set({ weekday: luxonDays[day] });

  if (startDate >= endDate) {
    let startOfDay = DateTime.fromFormat('12:00 AM', 'h:mm a');
    startOfDay = startOfDay.set({ weekday: luxonDays[day] });

    let endOfDay = DateTime.fromFormat('11:59 PM', 'h:mm a');
    endOfDay = endOfDay.set({ weekday: luxonDays[day] });

    return [
      Interval.fromDateTimes(startOfDay, endDate),
      Interval.fromDateTimes(startDate, endOfDay),
    ];
  }

  return [Interval.fromDateTimes(startDate, endDate)];
};

export const checkForConflicts = (recurringStatus1, recurringStatus2) =>
  recurringStatus1.days.some((day1) =>
    recurringStatus2.days.some((day2) => {
      const interval1 = getIntervalFromTimes(
        recurringStatus1.startTime,
        recurringStatus1.endTime,
        day1
      );
      const interval2 = getIntervalFromTimes(
        recurringStatus2.startTime,
        recurringStatus2.endTime,
        day2
      );

      return interval1.some((i1) => interval2.some((i2) => i1.overlaps(i2)));
    })
  );

export const formatMessageTimestamp = (timestamp) =>
  // Format - 3/30/2021 3:24PM
  DateTime.fromISO(timestamp).toFormat('M/d/y h:mma');

export const formatMessagesSeparator = (date) =>
  // Format - Tuesday, March 30, 2021
  DateTime.fromFormat(date, 'M/d/yyyy').toFormat('cccc, MMMM d, y');

export const isDateBeforeCurrentDate = (date) => {
  if (!date) {
    return false;
  }

  return DateTime.fromISO(date) < DateTime.now();
};

export const isDateAfterCurrentDate = (date) => {
  if (!date) {
    return false;
  }

  return DateTime.fromISO(date) > DateTime.now();
};

export const getDifferenceBetweenDatesInMilliseconds = (date) => {
  const incomingDate = DateTime.fromISO(date);
  const currentDate = DateTime.now();

  return incomingDate.diff(currentDate).milliseconds;
};
